import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getLocationMapConfig,
  getLocationOptionDetails,
  reverseGeocodeLocation,
  searchLocationOptions,
} from "../storage";

const DEFAULT_ATTRIBUTION = "Powered by Google";
const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 350;

export function hasLocationCoordinates(value) {
  return Number.isFinite(Number(value?.latitude)) && Number.isFinite(Number(value?.longitude));
}

function normalizeCoordinate(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return numeric.toFixed(7);
}

function createSessionToken() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `places-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

let googleMapsLoaderPromise = null;

function loadGoogleMapsScript(apiKey, languageCode, regionCode) {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Maps can only load in the browser."));
  }
  if (window.google?.maps) {
    return Promise.resolve(window.google);
  }
  if (googleMapsLoaderPromise) {
    return googleMapsLoaderPromise;
  }

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-google-maps-loader="true"]');
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google));
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Google Maps.")));
      return;
    }

    const script = document.createElement("script");
    const params = new URLSearchParams({
      key: apiKey,
      v: "weekly",
    });
    if (languageCode) params.set("language", languageCode);
    if (regionCode) params.set("region", regionCode);

    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = "true";
    script.onload = () => {
      if (window.google?.maps) {
        resolve(window.google);
        return;
      }
      reject(new Error("Google Maps API loaded without maps namespace."));
    };
    script.onerror = () => reject(new Error("Failed to load Google Maps."));
    document.head.appendChild(script);
  });

  return googleMapsLoaderPromise;
}

function buildOpenMapUrl({ latitude, longitude }) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
}

export default function MapLocationPicker({
  id,
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  searchPlaceholder = "Search location from Google",
}) {
  const [query, setQuery] = useState(() => String(value?.address || ""));
  const [results, setResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const [resolvingPlaceId, setResolvingPlaceId] = useState("");
  const [status, setStatus] = useState("");
  const [attribution, setAttribution] = useState(DEFAULT_ATTRIBUTION);
  const [mapConfig, setMapConfig] = useState({
    embedApiKey: "",
    languageCode: "en",
    regionCode: "IN",
  });
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");
  const cacheRef = useRef(new Map());
  const requestIdRef = useRef(0);
  const optionRefs = useRef([]);
  const rootRef = useRef(null);
  const sessionTokenRef = useRef("");
  const skipExternalClearSyncRef = useRef(false);
  const mapConfigLoadedRef = useRef(false);
  const mapCanvasRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapMarkerRef = useRef(null);
  const markerDragStartPositionRef = useRef(null);

  const address = String(value?.address || "");
  const hasCoordinates = hasLocationCoordinates(value);
  const openMapUrl = buildOpenMapUrl({
    latitude: value?.latitude,
    longitude: value?.longitude,
  });

  useEffect(() => {
    if (skipExternalClearSyncRef.current && !address) {
      skipExternalClearSyncRef.current = false;
      return;
    }
    setQuery(address);
  }, [address]);

  useEffect(() => {
    if (activeIndex < 0) return;
    const target = optionRefs.current[activeIndex];
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, results]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (!hasCoordinates || mapConfigLoadedRef.current) return;
    mapConfigLoadedRef.current = true;
    let active = true;
    const loadMapConfig = async () => {
      try {
        const config = await getLocationMapConfig();
        if (!active) return;
        setMapConfig({
          embedApiKey: String(config?.embedApiKey || ""),
          languageCode: String(config?.languageCode || "en"),
          regionCode: String(config?.regionCode || "IN"),
        });
        if (config?.attribution) {
          setAttribution(String(config.attribution));
        }
      } catch (err) {
        console.error(err);
        if (active) {
          setMapError(err?.message || "Failed to load Google map configuration.");
        }
      }
    };
    void loadMapConfig();
    return () => {
      active = false;
    };
  }, [hasCoordinates]);

  const clearSelectionForNewSearch = (nextQuery) => {
    setQuery(nextQuery);
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
    sessionTokenRef.current = "";
    if (address || hasCoordinates) {
      skipExternalClearSyncRef.current = true;
      onChange({ address: "", latitude: "", longitude: "" });
    }
    setStatus(nextQuery.trim() ? "Select the correct Google address from the dropdown." : "");
  };

  const applySelection = async (result) => {
    const placeId = String(result?.placeId || "").trim();
    if (!placeId) return;

    const activeSessionToken = sessionTokenRef.current || createSessionToken();
    sessionTokenRef.current = activeSessionToken;
    setResolvingPlaceId(placeId);
    setStatus("Fetching exact Google location details...");
    try {
      const details = await getLocationOptionDetails(placeId, activeSessionToken);
      const nextAddress = String(details?.address || details?.displayName || result?.displayName || "").trim();
      onChange({
        address: nextAddress,
        latitude: normalizeCoordinate(details?.latitude),
        longitude: normalizeCoordinate(details?.longitude),
      });
      setAttribution(details?.attribution || DEFAULT_ATTRIBUTION);
      setQuery(nextAddress);
      setResults([]);
      setOpen(false);
      setActiveIndex(-1);
      sessionTokenRef.current = "";
      setStatus("Location selected. Exact Google coordinates captured.");
    } catch (err) {
      console.error(err);
      setStatus(err?.message || "Failed to resolve Google place details.");
    } finally {
      setResolvingPlaceId("");
    }
  };

  const runSearch = useCallback(async (searchText, options = {}) => {
    const { preserveResults = false } = options;
    const trimmed = String(searchText || "").trim();
    if (trimmed.length < 3) {
      if (!preserveResults) {
        setResults([]);
        setOpen(false);
        setActiveIndex(-1);
      }
      setStatus(`Enter at least ${MIN_QUERY_LENGTH} characters to search Google addresses.`);
      return;
    }

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = createSessionToken();
    }

    const cacheKey = trimmed.toLowerCase();
    if (cacheRef.current.has(cacheKey)) {
      const cachedResponse = cacheRef.current.get(cacheKey);
      const cachedResults = Array.isArray(cachedResponse?.results) ? cachedResponse.results : [];
      setResults(cachedResults);
      setAttribution(cachedResponse?.attribution || DEFAULT_ATTRIBUTION);
      setOpen(cachedResults.length > 0);
      setActiveIndex(cachedResults.length > 0 ? 0 : -1);
      setStatus(cachedResults.length > 0 ? "Select the correct Google address." : "No Google addresses found.");
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setStatus("Searching Google addresses...");
    try {
      const response = await searchLocationOptions(trimmed, sessionTokenRef.current);
      if (requestId !== requestIdRef.current) return;
      const nextResults = Array.isArray(response?.results) ? response.results : [];
      const nextAttribution = response?.attribution || DEFAULT_ATTRIBUTION;
      cacheRef.current.set(cacheKey, { results: nextResults, attribution: nextAttribution });
      setResults(nextResults);
      setAttribution(nextAttribution);
      setOpen(nextResults.length > 0);
      setActiveIndex(nextResults.length > 0 ? 0 : -1);
      setStatus(nextResults.length > 0 ? "Select the correct Google address." : "No Google addresses found.");
    } catch (err) {
      console.error(err);
      if (requestId !== requestIdRef.current) return;
      setResults([]);
      setOpen(false);
      setActiveIndex(-1);
      setStatus(err?.message || "Failed to search Google addresses.");
    }
  }, []);

  useEffect(() => {
    if (resolvingPlaceId) return undefined;
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setOpen(false);
      setActiveIndex(-1);
      setStatus("");
      return undefined;
    }
    if (trimmed === address.trim() && hasCoordinates) {
      return undefined;
    }
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setOpen(false);
      setActiveIndex(-1);
      setStatus(`Enter at least ${MIN_QUERY_LENGTH} characters to search Google addresses.`);
      return undefined;
    }

    const timer = setTimeout(() => {
      void runSearch(trimmed);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [address, hasCoordinates, query, resolvingPlaceId, runSearch]);

  useEffect(() => {
    if (!hasCoordinates || !mapConfig.embedApiKey || !mapCanvasRef.current) return undefined;
    const latitude = Number(value?.latitude);
    const longitude = Number(value?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;

    let active = true;
    const position = { lat: latitude, lng: longitude };

    const ensureMap = async () => {
      setMapLoading(true);
      setMapError("");
      try {
        const google = await loadGoogleMapsScript(
          mapConfig.embedApiKey,
          mapConfig.languageCode,
          mapConfig.regionCode
        );
        if (!active || !mapCanvasRef.current) return;

        const currentMapDiv =
          mapInstanceRef.current && typeof mapInstanceRef.current.getDiv === "function"
            ? mapInstanceRef.current.getDiv()
            : null;
        const shouldCreateNewMap = !mapInstanceRef.current || currentMapDiv !== mapCanvasRef.current;

        if (shouldCreateNewMap) {
          mapInstanceRef.current = new google.maps.Map(mapCanvasRef.current, {
            center: position,
            zoom: 17,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            gestureHandling: "greedy",
          });
          mapMarkerRef.current = null;
        } else {
          mapInstanceRef.current.setCenter(position);
        }

        if (!mapMarkerRef.current) {
          mapMarkerRef.current = new google.maps.Marker({
            map: mapInstanceRef.current,
            position,
            draggable: true,
            title: label,
          });

          mapMarkerRef.current.addListener("dragstart", () => {
            const currentPosition = mapMarkerRef.current?.getPosition?.();
            markerDragStartPositionRef.current = currentPosition
              ? {
                  lat: currentPosition.lat(),
                  lng: currentPosition.lng(),
                }
              : {
                  lat: latitude,
                  lng: longitude,
                };
            setStatus("Drag the pin and release it to update the address.");
          });

          mapMarkerRef.current.addListener("dragend", async (event) => {
            const lat = Number(event?.latLng?.lat?.());
            const lng = Number(event?.latLng?.lng?.());
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
              return;
            }
            setStatus("Updating address from dragged map pin...");
            try {
              const response = await reverseGeocodeLocation(lat, lng);
              const nextAddress = String(
                response?.address || response?.displayName || `${lat.toFixed(7)}, ${lng.toFixed(7)}`
              ).trim();
              onChange({
                address: nextAddress,
                latitude: normalizeCoordinate(lat),
                longitude: normalizeCoordinate(lng),
              });
              setQuery(nextAddress);
              setStatus("Location updated from dragged map pin.");
              if (response?.attribution) {
                setAttribution(String(response.attribution));
              }
              if (mapInstanceRef.current) {
                mapInstanceRef.current.panTo({ lat, lng });
              }
            } catch (err) {
              console.error(err);
              const fallback = markerDragStartPositionRef.current;
              if (fallback && mapMarkerRef.current) {
                mapMarkerRef.current.setPosition(fallback);
                if (mapInstanceRef.current) {
                  mapInstanceRef.current.panTo(fallback);
                }
              }
              setStatus(err?.message || "Failed to update address from dragged map pin.");
            }
          });
        } else {
          mapMarkerRef.current.setPosition(position);
          mapMarkerRef.current.setMap(mapInstanceRef.current);
        }
      } catch (err) {
        console.error(err);
        if (active) {
          setMapError(err?.message || "Failed to load interactive Google map.");
        }
      } finally {
        if (active) {
          setMapLoading(false);
        }
      }
    };

    void ensureMap();

    return () => {
      active = false;
    };
  }, [
    hasCoordinates,
    label,
    mapConfig.embedApiKey,
    mapConfig.languageCode,
    mapConfig.regionCode,
    onChange,
    value?.latitude,
    value?.longitude,
  ]);

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown" && results.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => Math.min(prev + 1, results.length - 1));
      return;
    }
    if (event.key === "ArrowUp" && results.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (open && results.length > 0) {
        void applySelection(results[activeIndex >= 0 ? activeIndex : 0]);
        return;
      }
      void runSearch(query);
    }
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <div
        style={{
          fontSize: "0.78rem",
          fontWeight: 800,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#475569",
          marginBottom: 8,
        }}
      >
        {label}
        {required ? " *" : ""}
      </div>

      <input
        id={id}
        type="text"
        className="input-field"
        value={query}
        onChange={(event) => clearSelectionForNewSearch(event.target.value)}
        onFocus={() => {
          if (results.length > 0) {
            setOpen(true);
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={searchPlaceholder}
        autoComplete="off"
        disabled={disabled || !!resolvingPlaceId}
      />

      {open && results.length > 0 ? (
        <div
          className="autocomplete-dropdown"
          style={{
            position: "relative",
            top: "auto",
            left: "auto",
            right: "auto",
            marginTop: 8,
            maxHeight: 240,
          }}
        >
          {results.map((result, index) => {
            const isActive = index === activeIndex;
            const isResolving = resolvingPlaceId && resolvingPlaceId === result.placeId;
            return (
              <button
                key={`${result.placeId}-${index}`}
                ref={(element) => {
                  optionRefs.current[index] = element;
                }}
                type="button"
                onClick={() => void applySelection(result)}
                onMouseEnter={() => setActiveIndex(index)}
                disabled={!!resolvingPlaceId}
                style={{
                  width: "100%",
                  border: "none",
                  borderBottom: index === results.length - 1 ? "none" : "1px solid rgba(226, 232, 240, 0.9)",
                  background: isActive ? "rgba(219, 234, 254, 0.9)" : "#ffffff",
                  textAlign: "left",
                  padding: "10px 12px",
                  cursor: resolvingPlaceId ? "wait" : "pointer",
                  opacity: resolvingPlaceId && !isResolving ? 0.72 : 1,
                }}
              >
                <div style={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.35 }}>
                  {result.primaryText || result.displayName}
                </div>
                <div style={{ marginTop: 4, fontSize: "0.8rem", color: "#64748b", lineHeight: 1.35 }}>
                  {result.secondaryText || result.displayName}
                </div>
                {isResolving ? (
                  <div style={{ marginTop: 5, fontSize: "0.76rem", color: "#1d4ed8", fontWeight: 700 }}>
                    Resolving exact coordinates...
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      <div
        style={{
          marginTop: 8,
          padding: "10px 12px",
          borderRadius: 12,
          border: hasCoordinates ? "1px solid rgba(34, 197, 94, 0.25)" : "1px solid rgba(148, 163, 184, 0.2)",
          background: hasCoordinates ? "rgba(240, 253, 244, 0.9)" : "rgba(248, 250, 252, 0.95)",
        }}
      >
        <div
          style={{
            fontSize: "0.74rem",
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: hasCoordinates ? "#15803d" : "#64748b",
          }}
        >
          {hasCoordinates ? "Selected Delivery Location" : "Location Selection"}
        </div>
        <div style={{ marginTop: 6, color: "#0f172a", fontWeight: 700, lineHeight: 1.45, wordBreak: "break-word" }}>
          {hasCoordinates
            ? address
            : "Start typing the address, then select the correct Google result to capture the exact delivery coordinates."}
        </div>
        <div style={{ marginTop: 6, fontSize: "0.82rem", color: hasCoordinates ? "#166534" : "#64748b" }}>
          {hasCoordinates
            ? `Lat ${normalizeCoordinate(value?.latitude)} | Lng ${normalizeCoordinate(value?.longitude)}`
            : "Latitude and longitude are captured only after a Google address is selected."}
        </div>
      </div>

      {status ? (
        <div
          style={{
            marginTop: 8,
            fontSize: "0.82rem",
            color: status.toLowerCase().includes("failed") ? "#b91c1c" : "#475569",
            fontWeight: 700,
          }}
        >
          {status}
        </div>
      ) : null}

      {hasCoordinates ? (
        <div
          style={{
            marginTop: 12,
            padding: "12px",
            borderRadius: 14,
            border: "1px solid rgba(148, 163, 184, 0.18)",
            background: "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.96))",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 10,
            }}
          >
            <div
              style={{
                fontSize: "0.78rem",
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#334155",
              }}
            >
              Interactive Map
            </div>
            {openMapUrl ? (
              <a
                href={openMapUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-secondary"
                style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}
              >
                Open In Google Maps
              </a>
            ) : null}
          </div>

          <div style={{ fontSize: "0.82rem", color: "#475569", fontWeight: 700, marginBottom: 10 }}>
            Drag the pin on the map to fine-tune the delivery address.
          </div>

          {mapError ? (
            <div style={{ fontSize: "0.84rem", color: "#b91c1c", fontWeight: 700 }}>{mapError}</div>
          ) : (
            <div
              style={{
                borderRadius: 14,
                overflow: "hidden",
                border: "1px solid rgba(148, 163, 184, 0.18)",
                boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)",
                position: "relative",
              }}
            >
              {mapLoading ? (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.82)",
                    color: "#334155",
                    fontWeight: 800,
                  }}
                >
                  Loading interactive map...
                </div>
              ) : null}
              <div
                ref={mapCanvasRef}
                style={{ width: "100%", minHeight: 260, display: "block" }}
              />
            </div>
          )}
        </div>
      ) : null}

      <div style={{ marginTop: 8, fontSize: "0.74rem", color: "#64748b", fontWeight: 700 }}>{attribution}</div>
    </div>
  );
}
